import { z } from "zod";
import { SuccessMetaSchema, ShippingRuleSchema } from "../common";
import { TaxSettingsSchema } from "../taxes";
import {
  ModalDisplaySchema,
  BannerDisplaySchema,
  PricingLogicSchema,
  OffersEmbedSchema,
  ProductTargetBaseSchema,
  BundleDisplaySchema,
} from "../upsell-constructs";
import { OtpChannelEnum } from "./otp.schema";
import { OtpUiTextsSchema } from "./otp-config.schema";
import { BootstrapTrackingSchema } from "./pixels.schema";

// =========================================================================
// POST /forms/bootstrap - REQUEST
// =========================================================================

export const CartItemSchema = z.object({
  variant_id: z.number().int().positive(),
  quantity: z.number().int().positive().default(1),
});

export const BootstrapRequestSchema = z.object({
  shop_domain: z
    .string()
    .min(1)
    .regex(/\.myshopify\.com$/, "Invalid Shopify domain"),
  cart_items: z.array(CartItemSchema).optional(),
});

// =========================================================================
// SECCIONES DE RESPUESTA
// =========================================================================

// --- 1. VISUAL DESIGN ---
export const VisualDesignSchema = z.object({
  template_slug: z.string(),
  layout_mode: z.enum(["single_step", "multi_step", "accordion"]),

  // .loose() permite que cualquier propiedad extra en el JSONB de la DB
  // (ej: logo_url, shadow_depth) pase al frontend sin ser eliminada.
  styles: z
    .object({
      // Propiedades contractuales (El frontend espera esto sí o sí para no romperse)
      primary_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/, "Invalid hex color"),
      background_color: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
      font_family: z.string(),
      border_radius: z.number().int().min(0).max(50),
      custom_css: z.string().optional(),
    })
    .loose(),

  texts: z
    .object({
      submit_btn: z.string(),
      modal_title: z.string().optional(),
      thank_you_title: z.string(),
      redirect_url: z.string().optional(),
      errors: z.record(z.string(), z.string()).optional(),
    })
    .loose(),

  component_styles: z
    .object({
      // El bloque que muestras en la imagen
      shipping_method: z
        .object({
          background_color: z.string().optional(),
          border_color: z.string().optional(),
          active_border_color: z.string().optional(),
          show_radio_button: z.boolean().default(true),
        })
        .optional(),

      // El bloque de resumen de orden (arriba)
      order_summary: z
        .object({
          background_color: z.string().optional(),
          subtotal_text: z.string().optional(),
          shipping_text: z.string().optional(),
          total_text: z.string().optional(),
          show_tax_notice: z.boolean().optional(),
          border_color: z.string().optional(),
          border_width: z.number().optional(),
        })
        .optional(),
    })
    .optional(),
});

// --- 2. FORM FIELDS ---

// Propiedades base que todo bloque/campo tiene
const BaseFieldSchema = z.object({
  key: z.string(),
  label: z.string(),
  visible: z.boolean().default(true),
  width: z.string().default("100%"),
  display_order: z.number().int().optional(),
  show_icon: z.boolean().optional(),
  border_color: z.string().optional(),
  field_background_color: z.string().optional(),
});

// Variante: Inputs de Texto (Text, Email, Textarea)
const TextFieldSchema = BaseFieldSchema.extend({
  type: z.enum(["text", "email", "textarea", "date", "number"]),
  placeholder: z.string().optional(),
  required: z.boolean().default(false),
  validation_regex: z.string().optional(),
  min_length: z.number().int().optional(),
  max_length: z.number().int().optional(),
  google_autocomplete: z.boolean().optional(),
});

// Variante: Teléfono
const TelFieldSchema = BaseFieldSchema.extend({
  type: z.literal("tel"),
  placeholder: z.string().optional(),
  required: z.boolean().default(false),
  prefix: z.string().optional(),
  validation_regex: z.string().optional(),
});

// Variante: Selects
const SelectFieldSchema = BaseFieldSchema.extend({
  type: z.literal("select"),
  placeholder: z.string().optional(),
  required: z.boolean().default(false),
  source: z.enum(["static", "remote_geo_json"]).optional(),
  disabled_message: z.string().optional(),
  // Opciones estáticas si source='static' (Vienen del JSON options en config)
  options: z
    .array(
      z.object({
        label: z.string(),
        value: z.string(),
      })
    )
    .optional(),
});

// Variante: Checkbox
const CheckboxFieldSchema = BaseFieldSchema.extend({
  type: z.literal("checkbox"),
  required: z.boolean().default(false),
  placeholder: z.string().optional(),
});

// Variante: Order Summary
const OrderSummaryFieldSchema = BaseFieldSchema.extend({
  type: z.literal("order_summary"),
  required: z.boolean().default(false),
});

// Variante: Shipping Method
const ShippingMethodFieldSchema = BaseFieldSchema.extend({
  type: z.literal("shipping_method"),
  required: z.boolean().default(false),
});

export const OffersEmbedFieldSchema = OffersEmbedSchema;

// Variante: Bloque de Imagen (Comentado para referencia, fácil de activar)
/*
const ImageBlockSchema = BaseFieldSchema.extend({
  type: z.literal("image_block"),
  src: z.string().url(),
  alt: z.string().optional(),
  // No tiene 'required' ni 'placeholder'
});
*/

export const FormFieldSchema = z.discriminatedUnion("type", [
  TextFieldSchema,
  TelFieldSchema,
  SelectFieldSchema,
  CheckboxFieldSchema,
  OrderSummaryFieldSchema,
  ShippingMethodFieldSchema,
  OffersEmbedFieldSchema,
  // Bloques nuevos se agregan aquí
  // ImageBlockSchema,
]);

// --- 3. LOGISTICS ---

// Estructura exacta de shops.shipping_settings
export const LogisticsSchema = z.object({
  // Coverage Settings
  coverage: z.object({
    min_score: z.number().min(0).max(100).default(0),
    excluded_cities: z.array(z.string()).default([]), // Códigos de ciudad
  }),

  // Office Delivery Settings
  offices: z.object({
    enabled: z.boolean().default(false),
    min_score: z.number().min(0).max(100).default(80),
    label: z.string().optional(),
    fetch_endpoint: z.string().optional(),
  }),

  // Stack de Precios
  rates: z
    .array(
      ShippingRuleSchema.omit({ active: true }).extend({
        id: z.string(),
      })
    )
    .default([]),

  // Datos Auxiliares
  geo_data_url: z.string(),

  // Tax Settings (optional — missing means taxes disabled)
  // Omit for now
  tax: TaxSettingsSchema.omit({ specific_rates: true }).optional(),
});

// --- 4. SECURITY & TRACKING ---
export const SecuritySchema = z.object({
  otp_strategy: z.enum(["always", "high_risk_only", "disabled"]),
  channel: OtpChannelEnum,
  otp_length: z.number().default(6),

  /**
   * Horas de validez de una verificación previa.
   * El frontend puede cachear el verification_token y saltar el OTP si
   * el número ya fue verificado dentro de este periodo.
   */
  validity_period_hours: z.number().int().positive(),
  max_resends: z.number().int().positive(),

  /**
   * Todos los strings del widget OTP ya resueltos en el idioma del merchant.
   * El frontend no hardcodea ningún texto — consume este objeto.
   */
  otp_ui_texts: OtpUiTextsSchema,
});

// --- 5. UPSELLS ---
const OfferProductTarget = ProductTargetBaseSchema.refine(
  (data) =>
    data.shopify_variant_id || data.shopify_product_id || data.bundle_id,
  {
    message: "Debe especificarse un variant_id, product_id o un bundle_id",
  }
);

// Shared base avoids duplicating display_data / pricing_logic across offer payloads.
const BaseModalPayload = z.object({
  display_data: ModalDisplaySchema,
  pricing_logic: PricingLogicSchema,
});

// Upsell modals require a product target and cannot use on_close placement.
const ModalOfferPayload = BaseModalPayload.extend({
  display_type: z.literal("modal"),
  placement: z.enum(["before_complete", "after_complete"]),
  product_target: OfferProductTarget,
});

// --- (Futuro) Payload para un banner ---
const BannerOfferPayload = z.object({
  display_type: z.literal("banner"),
  display_data: BannerDisplaySchema,
  pricing_logic: PricingLogicSchema,
  product_target: OfferProductTarget,
});

const BundleOfferPayload = z.object({
  display_type: z.literal("bundle"),
  display_data: BundleDisplaySchema,
  pricing_logic: PricingLogicSchema,
  // Sin product_target — los variant_ids viven dentro de items_definition
});

export const BootstrapUpsellOfferSchema = z
  .object({
    offer_id: z.string(),

    /**
     * Reglas que el frontend puede usar para decidir si muestra la oferta.
     * (Ej: { product_ids: [123, 456] }), vacío significa que siempre se muestra.
     * Común a todas las formas.
     */
    trigger_rules: z.record(z.string(), z.any()),
  })
  .and(
    z.discriminatedUnion("display_type", [
      ModalOfferPayload,
      BannerOfferPayload,
      BundleOfferPayload,
    ])
  );

// --- 6. DOWNSELLS ---
// Downsells are on_close modals — always synthetic (no product_target).
const DownsellOfferPayload = BaseModalPayload.extend({
  display_type: z.literal("modal"),
  placement: z.literal("on_close"),
});

export const BootstrapDownsellOfferSchema = z
  .object({
    offer_id: z.string(),
    trigger_rules: z.record(z.string(), z.any()),
  })
  .and(DownsellOfferPayload);

// =========================================================================
// RESPONSE COMPLETA
// =========================================================================

export const BootstrapResponseSchema = z.object({
  meta: SuccessMetaSchema.extend({
    shop_base_currency: z.string(),
    shop_name: z.string(),
    config_version_hash: z.string(),
    form_id: z.string(),
    abandoned_cart_enabled: z.boolean().optional(),
  }),
  visual_design: VisualDesignSchema,
  form_fields: z.array(FormFieldSchema),
  logistics: LogisticsSchema,
  security: SecuritySchema,
  tracking: BootstrapTrackingSchema,
  upsells: z.array(BootstrapUpsellOfferSchema),
  downsells: z.array(BootstrapDownsellOfferSchema),
});

export type BootstrapRequest = z.infer<typeof BootstrapRequestSchema>;
export type BootstrapResponse = z.infer<typeof BootstrapResponseSchema>;
export type FormField = z.infer<typeof FormFieldSchema>;
export type UpsellOffer = z.infer<typeof BootstrapUpsellOfferSchema>;
export type BootstrapDownsellOffer = z.infer<
  typeof BootstrapDownsellOfferSchema
>;
